/**
 * Checks if a function is callable.
 */
export const isCallable = funct => typeof funct === 'function'

/**
 * Checks if path exists within an object.
 */
export const hasPath = (path, target) => {
  let obj = target
  let previousPath = null
  let isNullOrNonObject = false
  const isValidPath = path.split('.').reduce((reducer, prop) => {
    if (obj == null || typeof obj !== 'object') {
      isNullOrNonObject = true
      return reducer && false
    }

    if (prop in obj) {
      obj = obj[prop]
      previousPath = previousPath === null ? prop : previousPath + '.' + prop

      return reducer && true
    }

    return reducer && false
  }, true)

  if (process.env.NODE_ENV !== 'production') {
    if (isNullOrNonObject) {
      throw new Error(previousPath + ' is not an object')
    }
  }

  return isValidPath
}

/**
 * Assign polyfill from the mdn.
 */
export const assign = (target, ...others) => {
  /* istanbul ignore else */
  if (isCallable(Object.assign)) {
    return Object.assign(target, ...others)
  }

  /* istanbul ignore next */
  if (target == null) {
    throw new TypeError('Cannot convert undefined or null to object')
  }

  /* istanbul ignore next */
  const to = Object(target)
  /* istanbul ignore next */
  others.forEach(arg => {
    // Skip over if undefined or null
    if (arg != null) {
      Object.keys(arg).forEach(key => {
        to[key] = arg[key]
      })
    }
  })
  /* istanbul ignore next */
  return to
}

/**
 * Gets the value in an object safely.
 */
export const getPath = (path, target, def = undefined) => {
  if (!path || !target) return def

  let value = target
  path.split('.').every(prop => {
    if (prop in value) {
      value = value[prop]

      return true
    }

    value = def

    return false
  })

  return value
}

/**
 * Gets the data attribute. the name must be kebab-case.
 */
export const getDataAttribute = (el, name) =>
  el.getAttribute(`data-vv-${name}`)

/**
 * Checks if the values are either null or undefined.
 */
export const isNullOrUndefined = (...values) => {
  return values.every(value => {
    return value === null || value === undefined
  })
}

/**
 * Determines the input field scope.
 */
export const getScope = el => {
  let scope = getDataAttribute(el, 'scope')
  if (isNullOrUndefined(scope)) {
    let form = getForm(el)

    if (form) {
      scope = getDataAttribute(form, 'scope')
    }
  }

  return !isNullOrUndefined(scope) ? scope : null
}

/**
 * Get the closest form element.
 */
export const getForm = el => {
  if (isNullOrUndefined(el)) return null

  if (el.tagName === 'FORM') return el

  if (!isNullOrUndefined(el.form)) return el.form

  return !isNullOrUndefined(el.parentNode) ? getForm(el.parentNode) : null
}

/**
 * Converts an array-like object to array, provides a simple polyfill for Array.from
 */
export const toArray = arrayLike => {
  if (isCallable(Array.from)) {
    return Array.from(arrayLike)
  }

  const array = []
  const length = arrayLike.length
  /* istanbul ignore next */
  for (let i = 0; i < length; i++) {
    array.push(arrayLike[i])
  }

  /* istanbul ignore next */
  return array
}

export const includes = (collection, item) => {
  return collection.indexOf(item) !== -1
}

/**
 * Checks if the value is an object.
 */
export const isObject = obj =>
  obj !== null && obj && typeof obj === 'object' && !Array.isArray(obj)

export const deepParseInt = input => {
  if (typeof input === 'number') return input

  if (typeof input === 'string') return parseInt(input)

  const map = {}
  for (const element in input) {
    map[element] = parseInt(input[element])
  }

  return map
}

export const isBuiltInComponent = vnode => {
  if (!vnode) {
    return false
  }

  const tag = vnode.componentOptions.tag

  return /^(keep-alive|transition|transition-group)$/.test(tag)
}

export const isEmptyArray = arr => {
  return Array.isArray(arr) && arr.length === 0
}

export const findRule = (errorBag, rule) => {
  return errorBag.find(e => e.rule === rule)
}
